import {
  IconDownload,
  IconExternalLink,
  IconEye,
  IconFile,
  IconLoader2,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import {
  useEmailAttachmentsListQuery,
  fetchEmailAttachmentBlob,
} from "../../../api/queries/receivablesQueries";
import type { EmailAttachmentMeta } from "../../../utils/interfaces";
import {
  attachmentNeedsMetadataBackfill,
  getNonInlineAttachments,
  isPreviewableAttachment,
  isReferenceAttachment,
  type EmailAttachmentEntry,
} from "../../../utils/emailAttachments";
import { formatFileSize } from "../../../utils/utilsFunctions/functions";
import { EmailAttachmentPreviewModal } from "./EmailAttachmentPreviewModal";

type EmailAttachmentListProps = {
  messageId: string;
  attachments?: Record<string, EmailAttachmentMeta> | null;
};

type PreviewTarget = {
  attachmentId: string;
  meta: EmailAttachmentMeta;
};

export function EmailAttachmentList({
  messageId,
  attachments,
}: EmailAttachmentListProps) {
  const shouldBackfill = attachmentNeedsMetadataBackfill(attachments);
  const { data: backfilledAttachments, isLoading: isBackfilling } =
    useEmailAttachmentsListQuery({
      messageId,
      enabled: shouldBackfill,
    });

  const resolvedAttachments = shouldBackfill
    ? backfilledAttachments
    : attachments;

  const visibleAttachments = useMemo(
    () => getNonInlineAttachments(resolvedAttachments),
    [resolvedAttachments],
  );

  const [previewTarget, setPreviewTarget] = useState<PreviewTarget | null>(
    null,
  );
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  if (shouldBackfill && isBackfilling) {
    return (
      <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
        <IconLoader2 size={14} className="animate-spin" />
        Loading attachments…
      </div>
    );
  }

  if (visibleAttachments.length === 0) {
    return null;
  }

  return (
    <>
      <div className="mt-3 border-t border-slate-200 pt-3">
        <p className="mb-2 text-xs font-medium text-slate-600">
          Attachments ({visibleAttachments.length})
        </p>
        <div className="flex flex-wrap gap-2">
          {visibleAttachments.map((entry) => (
            <AttachmentChip
              key={entry.id}
              messageId={messageId}
              entry={entry}
              isDownloading={downloadingId === entry.id}
              onPreview={() =>
                setPreviewTarget({
                  attachmentId: entry.id,
                  meta: entry.meta,
                })
              }
              onDownloadingChange={(downloading) =>
                setDownloadingId(downloading ? entry.id : null)
              }
            />
          ))}
        </div>
      </div>

      {previewTarget ? (
        <EmailAttachmentPreviewModal
          isOpen
          messageId={messageId}
          attachmentId={previewTarget.attachmentId}
          meta={previewTarget.meta}
          onClose={() => setPreviewTarget(null)}
        />
      ) : null}
    </>
  );
}

function AttachmentChip({
  messageId,
  entry,
  isDownloading,
  onPreview,
  onDownloadingChange,
}: {
  messageId: string;
  entry: EmailAttachmentEntry;
  isDownloading: boolean;
  onPreview: () => void;
  onDownloadingChange: (downloading: boolean) => void;
}) {
  const { id, meta } = entry;
  const isReference = isReferenceAttachment(meta);
  const canPreview = !isReference && isPreviewableAttachment(meta);

  const handleDownload = async () => {
    if (isReference && meta.sourceUrl) {
      window.open(meta.sourceUrl, "_blank", "noopener,noreferrer");
      return;
    }

    onDownloadingChange(true);
    try {
      const blob = await fetchEmailAttachmentBlob(
        messageId,
        id,
        "attachment",
        undefined,
        meta.filename,
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = meta.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      // Errors surface in preview modal; download failures are silent here.
    } finally {
      onDownloadingChange(false);
    }
  };

  return (
    <div className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs text-slate-700">
      {isReference ? (
        <IconExternalLink size={14} className="shrink-0 text-slate-500" />
      ) : (
        <IconFile size={14} className="shrink-0 text-slate-500" />
      )}
      <span
        className="max-w-[12rem] truncate font-medium"
        title={meta.filename}
      >
        {meta.filename}
      </span>
      {meta.size != null ? (
        <span className="shrink-0 text-slate-400">
          {formatFileSize(meta.size)}
        </span>
      ) : null}
      <div className="ml-0.5 flex shrink-0 items-center gap-0.5">
        {canPreview ? (
          <button
            type="button"
            onClick={onPreview}
            className="rounded p-1 text-slate-500 hover:bg-white hover:text-slate-800"
            aria-label={`Preview ${meta.filename}`}
            title="Preview"
          >
            <IconEye size={14} />
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => void handleDownload()}
          disabled={isDownloading}
          className="rounded p-1 text-slate-500 hover:bg-white hover:text-slate-800 disabled:opacity-50"
          aria-label={
            isReference ? `Open ${meta.filename}` : `Download ${meta.filename}`
          }
          title={isReference ? "Open link" : "Download"}
        >
          {isDownloading ? (
            <IconLoader2 size={14} className="animate-spin" />
          ) : isReference ? (
            <IconExternalLink size={14} />
          ) : (
            <IconDownload size={14} />
          )}
        </button>
      </div>
    </div>
  );
}
